import { useEffect, useRef, useState } from 'react';
import {
  BuildFingerprint, VERSION_POLL_MS, currentEntry, fetchLatest, isUpdated,
} from '../utils/build-version';

/**
 * True once the server is serving a newer FolioWorth than this tab runs.
 * Checks every five minutes and whenever the window regains focus; the API
 * commit seen on the first check is this tab's baseline (issue #35).
 */
export function useBuildVersion(fetchFn: typeof fetch = fetch, pollMs = VERSION_POLL_MS): boolean {
  const [updated, setUpdated] = useState(false);
  const running = useRef<BuildFingerprint | null>(null);

  useEffect(() => {
    if (import.meta.env.MODE === 'test' && fetchFn === fetch) return undefined;
    let cancelled = false;
    let inFlight = false;
    const check = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const latest = await fetchLatest(fetchFn);
        if (cancelled) return;
        if (!running.current) {
          running.current = { entry: currentEntry(), api: latest.api };
        }
        if (isUpdated(running.current, latest)) setUpdated(true);
      } finally {
        inFlight = false;
      }
    };
    void check();
    const id = window.setInterval(check, pollMs);
    const onFocus = () => { void check(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchFn, pollMs]);

  return updated;
}
